/**
 * useAppVersionGuard Hook
 *
 * Prevents a long-lived browser tab from running a stale JavaScript bundle
 * after a new version is deployed (the cause of recurring "stale app" issues
 * such as the admin dashboard suddenly failing to fetch).
 *
 * It compares the hashed entry bundle currently running in the page against the
 * one referenced by the freshly fetched index.html. When they differ, the page
 * is reloaded ONCE per browser session (guarded via sessionStorage) so there is
 * no possibility of a reload loop.
 */

import { useEffect } from 'react';

const RELOAD_FLAG = 'sb_version_autoreload';
const ENTRY_RE = /assets\/index-[A-Za-z0-9_-]+\.js/;

/**
 * Pure decision helper (unit-tested): reload only when both versions are known,
 * they differ, and we have not already auto-reloaded this session.
 * @param {string|null} running
 * @param {string|null} deployed
 * @param {boolean} alreadyReloaded
 * @returns {boolean}
 */
export const shouldReload = (running, deployed, alreadyReloaded) =>
  Boolean(running && deployed && running !== deployed && !alreadyReloaded);

/** Read the hashed entry bundle filename currently loaded in the document. */
const getRunningEntry = () => {
  if (typeof document === 'undefined') return null;
  const scripts = Array.from(document.querySelectorAll('script[src]'));
  for (const script of scripts) {
    const match = (script.getAttribute('src') || '').match(ENTRY_RE);
    if (match) return match[0];
  }
  return null;
};

/** Fetch the live index.html (bypassing cache) and read its entry bundle. */
const fetchDeployedEntry = async () => {
  const res = await fetch(`/?_v=${Date.now()}`, { cache: 'no-store' });
  if (!res.ok) return null;
  const html = await res.text();
  const match = html.match(ENTRY_RE);
  return match ? match[0] : null;
};

export const useAppVersionGuard = ({ intervalMs = 5 * 60 * 1000 } = {}) => {
  useEffect(() => {
    const running = getRunningEntry();
    if (!running) return; // Cannot determine the running version — do nothing.

    let cancelled = false;

    const check = async () => {
      if (cancelled) return;
      if (sessionStorage.getItem(RELOAD_FLAG)) return; // Already reloaded once this session.

      let deployed = null;
      try {
        deployed = await fetchDeployedEntry();
      } catch {
        return; // Offline / transient — ignore.
      }
      if (cancelled) return;

      if (shouldReload(running, deployed, false)) {
        sessionStorage.setItem(RELOAD_FLAG, deployed);
        window.location.reload();
      }
    };

    const onFocus = () => check();
    const initialTimer = setTimeout(check, 5000);
    const interval = setInterval(check, intervalMs);
    window.addEventListener('focus', onFocus);

    return () => {
      cancelled = true;
      clearTimeout(initialTimer);
      clearInterval(interval);
      window.removeEventListener('focus', onFocus);
    };
  }, [intervalMs]);
};

export default useAppVersionGuard;
